import React, { useContext, useEffect, useState } from 'react';
import HFinput from '../../../components/common/input/HFinput';
import { useForm } from 'react-hook-form';
import HFbutton from '../../../components/button/HFbutton';
import { MypageContext } from '../membership/MypageContext';
import { phoneFormat } from '../../../api/utils/format/numbers';
import { useAppDispatch } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';
import instance from '../../../api/axios/utils.ts/instance';
import TextWinds from '../../../components/common/TextWinds';
import { Link, useNavigate } from 'react-router-dom';
import { filterAndUpperCase } from '../../../api/utils/util-func';
import useSmsHook from '../../../hooks/useSmsHook';
import { timeLeftCheck, timeLeftToString } from '../../../hooks/useTimer';
import FormRow from '../../../components/common/input/FormRow';
import ErrorInput from '../../../components/common/ErrorInput';
import { emailRules, normalizePhone } from '../../../constants/validation';

type UserBasicDTO = {
  email: string;
  countryCode: string;
  phone: string;
  englishName: string;
};

interface ModifyBasicProps {
  isEdit?: boolean;
}
const ModifyBasic = ({ isEdit = true }: ModifyBasicProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userInfo } = useContext(MypageContext);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [isConfirm, setIsConfirm] = React.useState<boolean>(false);
  const [verifyId, setVerifyId] = useState<number>();
  const { sendVerifyNumber, checkVerifyNumber, timeLeft } = useSmsHook();

  // 영문 이름 유효성 검사 상수 추가
  const MAX_NAME_LENGTH = 20;
  const MIN_NAME_LENGTH = 2;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<UserBasicDTO>({
    values: {
      phone: userInfo?.phone,
      email: userInfo?.email,
      countryCode: userInfo?.countryCode,
      englishName: userInfo?.englishName,
    },
  });
  const phone = watch('phone');

  // 전화번호 변경 감지
  const isPhoneChanged = phone !== userInfo?.phone;

  const Alert = (msg: string) => {
    dispatch(openAlert({ message: msg }));
  };

  const onSubmit = (data: UserBasicDTO) => {
    // 전화번호가 변경되었는데, 인증을 안받은 경우
    if (isPhoneChanged && !isConfirm) {
      Alert('전화번호가 변경 되었습니다.\n인증을 다시 받아주세요');
      return;
    }

    putUserInfo(data);
  };

  const handlePhoneChange = (value: string) => {
    setValue('phone', normalizePhone(value));
  };

  // 인증 코드 전송
  const postSms = async () => {
    if (!isPhoneChanged) {
      // 전화번호 변경없이 요청의 경우
      // 플로우상 존재할 수 없지만 예외처리
      return;
    }

    sendVerifyNumber(phone).then((msg) => {
      Alert(msg);
    });
  };

  // 인증코드 검증
  const putSms = async () => {
    if (!verificationCode) {
      Alert('인증번호를 입력해주세요.');
      return;
    }
    checkVerifyNumber(phone, verificationCode).then((res) => {
      if (res.confirm) {
        setIsConfirm(true);
        setVerifyId(res.confirm);
      }
      Alert(res.msg);
    });
  };

  /**
   * 영문 이름 유효성 검사
   */
  const validateEnglishName = (name: string): string => {
    if (!name) return '';

    if (name.length < MIN_NAME_LENGTH) {
      return `영문 이름은 최소 ${MIN_NAME_LENGTH}자 이상이어야 합니다.`;
    }

    if (name.length > MAX_NAME_LENGTH) {
      return `영문 이름은 최대 ${MAX_NAME_LENGTH}자까지 가능합니다.`;
    }

    const isUpperCase = /^[A-Z\s]*$/.test(name);
    if (!isUpperCase) {
      return '영문 대문자만 입력 가능합니다.';
    }

    const hasOnlySpaces = /^\s*$/.test(name);
    if (hasOnlySpaces) {
      return '공백만으로는 이름을 구성할 수 없습니다.';
    }

    const hasConsecutiveSpaces = /\s{2,}/.test(name);
    if (hasConsecutiveSpaces) {
      return '연속된 공백은 사용할 수 없습니다.';
    }

    return '';
  };

  const putUserInfo = async (data: UserBasicDTO) => {
    try {
      const response = await instance.put('/v1/mypage/user', data);
      if (response.status === 200) {
        Alert('수정되었습니다.');
        navigate('/mypage/modify');
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form>
      <section className="flex justify-between">
        <TextWinds type="title_h4">기본 정보</TextWinds>
        {!isEdit && (
          <Link to="/mypage/modify/basic">
            <div className="flex cursor-pointer items-center rounded border border-purple5 px-2 hover:bg-purple2">
              <div className="text-purple5">수정하기</div>
              <img src="/svg/icon/write.svg" alt="modify" />
            </div>
          </Link>
        )}
      </section>
      <section className="mt-5 flex flex-col gap-5">
        <TextWinds type={'content'}>이름 : {userInfo?.name}</TextWinds>

        <div>
          <TextWinds type={'content'}>영어이름 (수료증 기재용)</TextWinds>
          <FormRow
            placeholder="20자 이내 영문 대문자로 입력해주세요."
            name="englishName"
            type="text"
            control={control}
            disabled={!isEdit}
            rules={{
              required: {
                value: true,
                message: '20자 이내 영문 대문자로 입력해주세요.',
              },
              validate: (value) => {
                const error = validateEnglishName(value);
                return error || undefined;
              },
            }}
          />
          {isEdit && <ErrorInput text={errors.englishName?.message} />}
        </div>
        <div>
          <TextWinds type={'content'}>이메일 주소</TextWinds>
          <FormRow
            type="text"
            name="email"
            control={control}
            rules={emailRules({ required: '이메일을 입력해주세요.' })}
            disabled={!isEdit}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="relative flex w-full">
            <HFinput
              label="전화번호"
              value={phoneFormat(watch('phone'))}
              onChange={(e) => handlePhoneChange(e.target.value)}
              disabled={!isEdit}
              type="tel"
              className={`${isEdit ? '' : 'text-gray5'}`}
            />
            {isPhoneChanged && (
              <div className="absolute right-1 top-8">
                <HFbutton height={40} onClick={postSms} disabled={isConfirm}>
                  {timeLeftCheck(timeLeft)
                    ? timeLeftToString(timeLeft)
                    : '인증번호'}
                </HFbutton>
              </div>
            )}
          </div>
          {isPhoneChanged && (
            <div className="relative flex w-full">
              <HFinput
                label="인증번호"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={!isEdit}
              />
              <div className="absolute right-1 top-8">
                <HFbutton height={40} onClick={putSms} disabled={isConfirm}>
                  {isConfirm ? '인증완료' : '인증확인'}
                </HFbutton>
              </div>
            </div>
          )}
        </div>
      </section>
      {isEdit && (
        <div className="mt-5 flex flex-col gap-5">
          <HFbutton onClick={handleSubmit(onSubmit)}>저장</HFbutton>
          <HFbutton
            type="primary-outlined"
            onClick={() => navigate('/mypage/modify')}
          >
            취소
          </HFbutton>
        </div>
      )}
    </form>
  );
};

export default ModifyBasic;
